// =============================================================================
// Stock data-access (Phase 2) — DRAFT for review, not yet wired into the app.
//
// Maps the app's existing stock actions to the validated Supabase schema
// (stock_requests + stock_activity). The READS and the realtime SUBSCRIPTION are
// ready; the WRITES need the ~15-min FOH pass attached to the client (see the
// auth-pin Edge Function) so the app_session RLS policies accept them — that's
// the piece to finish once auth is wired.
//
// Realtime replaces the kitchen's 5-second poll (REFRESH_INTERVAL_MS in
// boh-kitchen.jsx): the kitchen subscribes once and gets every new request/flag
// pushed the instant it's tapped.
// =============================================================================
import { supabase } from "./supabaseClient.js";

// The London calendar date (YYYY-MM-DD) — use this for request_date so stock made
// just after midnight (BST) files under the right day, not the UTC day. (The DB
// column also defaults to the London date as a backstop; the app should pass it.)
export function londonToday() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
}

// --- Reads -------------------------------------------------------------------
export async function listOpenRequests(siteId) {
  return supabase.from("stock_requests")
    .select("*").eq("site_id", siteId).eq("status", "requested")
    .order("requested_at", { ascending: true });
}

export async function listRecentActivity(siteId, sinceIso) {
  let q = supabase.from("stock_activity").select("*").eq("site_id", siteId)
    .order("created_at", { ascending: false }).limit(200);
  if (sinceIso) q = q.gte("created_at", sinceIso);
  return q;
}

// --- Writes (need the FOH pass on the client) --------------------------------
// NO idem_key on the insert: the `stock_requests_one_open` partial unique index
// (site, item, request_date WHERE status='requested') is the correct dedup for
// open requests — a double-tap collapses onto it. Setting a (site|item|day)
// idem_key ALSO blocked the legitimate same-day RE-REQUEST after a delivery
// closed the first row (the "XL Black Gloves" class): the re-insert collided on
// the idem unique even though the one-open index would have allowed it (the old
// row is now 'received'). Dropping the idem lets the reopen land; the one-open
// index still prevents two simultaneous opens. (closeOpenRequests frees the
// index by flipping the delivered row to 'received' — the two together are what
// actually make a same-day re-request work.)
export async function createStockRequest({ siteId, itemName, catalogId = null, type = "standard", requestDate = londonToday(), requestedBy = null }) {
  // requested_by, when given, is stamped EXPLICITLY (durable-outbox path) so a
  // flushed-later row attributes to the tapper, not whatever session is warm at
  // flush time. The 0006 trigger only fills it from the JWT when it is null, so a
  // supplied value is preserved.
  const row = { site_id: siteId, item_name: itemName, catalog_id: catalogId, type, request_date: requestDate };
  if (requestedBy) row.requested_by = requestedBy;
  return supabase.from("stock_requests").insert(row).select().single();
}

export async function undoStockRequest({ siteId, itemName, requestDate }) {
  return supabase.from("stock_requests")
    .update({ status: "undone", updated_at: new Date().toISOString() })
    .eq("site_id", siteId).eq("item_name", itemName).eq("request_date", requestDate).eq("status", "requested");
}

export async function markReceived({ siteId, itemName, requestDate, receivedBy }) {
  return supabase.from("stock_requests")
    .update({ status: "received", received_by: receivedBy, received_at: new Date().toISOString() })
    .eq("site_id", siteId).eq("item_name", itemName).eq("request_date", requestDate).eq("status", "requested");
}

// A kitchen delivery closes the request in the real world, but until 17 Jul the
// stock_requests row stayed status='requested' forever (nothing ever wrote
// 'received' — the FOH button was removed). That stale open row blocks a
// SAME-DAY re-request via the stock_requests_one_open unique index: the fresh
// tap's insert collides and is swallowed, so the re-request never reaches
// Supabase at all (seen live: XL Black Gloves, arndale, 17 Jul). Closing the
// open row(s) on delivery keeps the DB honest AND frees the index for a
// genuine re-request the same day. Best-effort, called via shadow().
// deliveredAt = the instant of the delivery this close belongs to (the delivered
// activity row's created_at). We ONLY close requests raised AT OR BEFORE that
// instant — so a NEW request tapped in the race between the delivered tap and
// this write (requested_at > deliveredAt) is left OPEN, not silently closed.
// received_at is stamped with deliveredAt so the reader's synthesized 'received'
// event lines up with the delivery in the timeline.
export async function closeOpenRequests({ siteId, itemName, deliveredAt = new Date().toISOString(), receivedBy = null }) {
  // received_by, when given, is stamped explicitly so a delayed-flush auto-close
  // attributes to the delivery TAPPER, not whatever session is warm at flush time
  // (the 0006 trigger only fills received_by from the JWT when it is null).
  const patch = { status: "received", received_at: deliveredAt, updated_at: new Date().toISOString() };
  if (receivedBy) patch.received_by = receivedBy;
  return supabase.from("stock_requests")
    .update(patch)
    .eq("site_id", siteId).eq("item_name", itemName).eq("status", "requested")
    .lte("requested_at", deliveredAt);
}

// Kitchen flags + delivery lifecycle -> stock_activity.
// event: running_low | out_of_stock | back_in_stock | ordered | delivered | wastage
//
// kitchenEventRow is the PURE row builder (exported so the unit test can pin it).
// Lane BP (13 Sep 2026): the three device-fix columns from migration 1300
// (device_lat / device_lng / device_accuracy_m — the tablet's own GPS at the tap,
// which the database turns into the kitchen offsite flag) are added ONLY when the
// caller supplied a usable fix. A row without one is byte-identical to before, so
// today's dual-run shadow never sends a column an un-migrated database would reject
// with a silent 400 (the PGRST204 trap). offsite itself is never sent: the 1300
// trigger stamps it server-side.
export function kitchenEventRow({ siteId, itemName, event, flag = null, ordered = false, supplierRef = null, eta = null, note = null, photoPath = null, qty = null, staffId = null, idemKey, deviceLat = null, deviceLng = null, deviceAccuracyM = null }) {
  const row = {
    site_id: siteId, item_name: itemName, event, flag, ordered,
    supplier_ref: supplierRef, eta, note, photo_path: photoPath, qty,
    staff_id: staffId, idem_key: idemKey,
  };
  const lat = Number(deviceLat), lng = Number(deviceLng);
  if (deviceLat != null && deviceLng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
    row.device_lat = lat;
    row.device_lng = lng;
    const acc = Number(deviceAccuracyM);
    row.device_accuracy_m = (deviceAccuracyM != null && Number.isFinite(acc)) ? acc : null;
  }
  return row;
}

export async function logKitchenEvent(args) {
  return supabase.from("stock_activity").insert(kitchenEventRow(args)).select().single();
}

// --- Realtime (replaces the 5-second poll) -----------------------------------
// Returns an unsubscribe function. Fires onChange for every insert/update on the
// site's stock_requests + stock_activity the instant it happens.
export function subscribeToStock(siteId, onChange) {
  const ch = supabase.channel(`stock:${siteId}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "stock_requests", filter: `site_id=eq.${siteId}` }, onChange)
    .on("postgres_changes", { event: "*", schema: "public", table: "stock_activity", filter: `site_id=eq.${siteId}` }, onChange)
    .subscribe();
  return () => supabase.removeChannel(ch);
}
